import { LoadingButton } from '@mui/lab';
import { Alert, Link, Stack, Typography } from '@mui/material';
import { TwoFactoryCheckForm, TwoFactoryForm } from 'app/pages/TwoFactory';
import { useAsyncAction } from 'app/services';
import { sessionCheck2Fa, sessionReset2Fa, sessionSetup2Fa } from 'app/store/session';
import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink, Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';

const TwoFactory = () => {
  const formRef = useRef();
  const { secret } = useSelector((state) => state.session);

  const { asyncAction: submitSetup, isLoading: isSetupLoading } = useAsyncAction(sessionSetup2Fa);
  const { asyncAction: submitCheck, isLoading: isCheckLoading, isError: isCheckError } = useAsyncAction(sessionCheck2Fa);
  const { asyncAction: reset } = useAsyncAction(sessionReset2Fa);
  const { path } = useRouteMatch();

  const handleClickNext = () => {
    submitSetup();
  };

  const handleClickBack = () => {
    reset();
  };

  const handleClickForm = () => {
    formRef.current?.handleSubmitForm();
  };

  const handleSubmitForm = (params) => {
    submitCheck(params.code);
  };

  const handleLogKey = (e) => {
    if (e.code === 'Enter' || e.code === 'NumpadEnter') {
      e.preventDefault();
      handleClickForm();
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleLogKey);

    return () => {
      document.removeEventListener('keydown', handleLogKey);
    };
  }, []);

  if (!secret) {
    return <Redirect to='/' />;
  }

  if (secret.setupQrCode) {
    if (path !== '/account/check-2fa') {
      return <Redirect to='/account/check-2fa' />;
    }
  }

  return (
    <Switch>
      <Route exact path='/account/setup-2fa'>
        <Stack
          direction='row'
          flexDirection='column'
          sx={(theme) => ({
            paddingBottom: theme.spacing(2),
            '.MuiTypography-h5': {
              marginBottom: theme.spacing(1),
            },
            '.MuiTypography-body2': {
              color: theme.palette.text.secondary,
            },
          })}>
          <Typography variant='h5'>Двухфакторная авторизация</Typography>
          <Typography variant='body2'>
            Отсканируйте в приложении аутентификаторе QR-код или введите секретный ключ, указанный ниже вручную. Чтобы подтвердить
            правильную настройку приложения.
          </Typography>
        </Stack>
        <TwoFactoryForm secretKey={secret.key} qrCode={secret.qr} />
        <Stack
          direction='column'
          alignItems='center'
          justifyContent='flex-start'
          spacing={2}
          sx={(theme) => ({
            marginTop: theme.spacing(3),
            '.MuiLoadingButton-root': {
              lineHeight: '22px',
              padding: theme.spacing(2),
            },
            '.MuiAlert-root': {
              width: '100%',
              marginBottom: theme.spacing(2),
              borderRadius: theme.shape.borderRadius,
            },
          })}>
          <LoadingButton variant='contained' onClick={handleClickNext} loading={isSetupLoading} fullWidth disableElevation>
            Продолжить
          </LoadingButton>
          <Typography variant='body2'>
            <Link to='#' variant='subtitle2' underline='none' onClick={handleClickBack} component={RouterLink}>
              Вернуться к входу
            </Link>
          </Typography>
        </Stack>
      </Route>
      <Route exact path='/account/check-2fa'>
        <Stack
          direction='row'
          flexDirection='column'
          sx={(theme) => ({
            paddingBottom: theme.spacing(2),
            '.MuiTypography-h5': {
              marginBottom: theme.spacing(1),
            },
            '.MuiTypography-body2': {
              color: theme.palette.text.secondary,
            },
          })}>
          <Typography variant='h5'>Двухфакторная авторизация</Typography>
          <Typography variant='body2'>
            Введите двухфакторный код подтверждения из приложения аутентификатора, чтобы проверить правильность его настройки.
          </Typography>
        </Stack>
        <TwoFactoryCheckForm ref={formRef} onSubmit={handleSubmitForm} />
        <Stack
          direction='column'
          alignItems='center'
          justifyContent='flex-start'
          spacing={2}
          sx={(theme) => ({
            marginTop: theme.spacing(3),
            '.MuiLoadingButton-root': {
              lineHeight: '22px',
              padding: theme.spacing(2),
            },
            '.MuiAlert-root': {
              width: '100%',
              marginBottom: theme.spacing(2),
              borderRadius: theme.shape.borderRadius,
            },
          })}>
          {isCheckError && (
            <Alert severity='error'>
              Не удалось проверить код подтверждения. Пожалуйста, проверьте правильность написания кода подтверждения.
            </Alert>
          )}
          <LoadingButton variant='contained' loading={isCheckLoading} onClick={handleClickForm} fullWidth disableElevation>
            Продолжить
          </LoadingButton>
          <Typography variant='body2'>
            <Link to='#' variant='subtitle2' underline='none' onClick={handleClickBack} component={RouterLink}>
              Вернуться к входу
            </Link>
          </Typography>
        </Stack>
      </Route>
    </Switch>
  );
};

export default TwoFactory;
